import { assign, createMachine } from 'xstate';
import {
  AuthenticationPromptType,
  Credentials,
  CredentialsEntity,
} from '../../../models/credentials';
import { ApplicationError } from '../../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { credentialsService, CredentialsService } from '../../services/credentialsService';
import { getBankIdAuthenticationType } from '../../../utilities/provider-utils';
import { BankIdAuthenticationType } from '../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingMobileBankIdAuthenticationContainer/organisms/BankIdAuthWithSupplementalContainer/BankIdAuthWithSupplementalContainer';
import { Provider } from '../../../models/provider';
import { CREDENTIALS_STATE_INVALID } from '../../../utilities/error';
import { POLL_INTERVAL } from '../utils/utils';
import { Typegen0 } from './bankIdMachine.typegen';

export type BankIdMachineContext = {
  credentials: CredentialsEntity;
  userClickedOpenBankId?: boolean;
  onAutoOpenBankIdWithAnalytics: (credentials: CredentialsEntity) => void;
  isMobile: boolean;
  canFinishFlow: boolean;
  bankIdTarget?: Exclude<AuthenticationPromptType, 'NONE'>;
  error?: ApplicationError;
};

export type BankIdMachineEvents =
  | { type: 'USER_CLICK_REMOTE_OPEN_BANK_ID_EVENT' }
  | { type: 'USER_CLICK_LOCAL_OPEN_BANK_ID_EVENT' }
  | { type: 'HANDOFF_DONE_EVENT'; credentials: CredentialsEntity };

export type BankIdMachineServices = { getCredentials: { data: CredentialsEntity } };

export type BankIdMachineProps = {
  initialContext: BankIdMachineContext;
  onDone: (credentials: CredentialsEntity) => void;
  initialState?: Exclude<Typegen0['matchesStates'], object>;
  service?: CredentialsService;
  pollInterval?: number;
};

export const createBankIdMachine = ({
  initialContext,
  onDone,
  initialState = 'CHECK_INITIAL_PARAMS',
  service = credentialsService,
  pollInterval = POLL_INTERVAL,
}: BankIdMachineProps) =>
  createMachine(
    {
      id: 'BankIdMachine',
      initial: initialState,
      context: initialContext,
      tsTypes: {} as import('./bankIdMachine.typegen').Typegen0,
      schema: {
        events: {} as BankIdMachineEvents,
        services: {} as BankIdMachineServices,
        context: {} as BankIdMachineContext,
      },
      predictableActionArguments: true,
      preserveActionOrder: true,
      states: {
        CHECK_INITIAL_PARAMS: {
          always: [
            {
              target: 'APPLICATION_ERROR',
              cond: 'check_if_credentials_in_invalid_state',
              actions: 'throw_credentials_invalid_state_error',
            },
            {
              target: 'UI_AND_POLLER',
            },
          ],
        },
        UI_AND_POLLER: {
          type: 'parallel',
          states: {
            POLLER: {
              initial: 'GET_CREDENTIALS',
              states: {
                GET_CREDENTIALS: {
                  invoke: {
                    src: 'getCredentials',
                    onDone: [
                      {
                        target: 'AWAIT',
                        actions: 'update_credentials_in_context',
                      },
                    ],
                    onError: [
                      {
                        target: '#BankIdMachine.APPLICATION_ERROR',
                        actions: 'set_error_in_context',
                      },
                    ],
                  },
                },
                AWAIT: {
                  after: {
                    POLL_INTERVAL: [
                      {
                        target: '#BankIdMachine.UI_AND_POLLER.POLLER.GET_CREDENTIALS',
                      },
                    ],
                  },
                },
              },
              always: {
                target: '#BankIdMachine.CHECK_FLOW_FINISHED',
                cond: 'check_if_credentials_status_changed',
              },
            },
            UI: {
              initial: 'CHECK_FORCED_VIEW',
              states: {
                CHECK_FORCED_VIEW: {
                  always: [
                    {
                      target: 'OPENING_BANK_ID_VIEW',
                      cond: 'check_if_forced_local',
                    },
                    {
                      target: 'REMOTE_VIEW',
                      cond: 'check_if_forced_remote',
                    },
                    {
                      target: 'MOBILE_SELECT_TARGET_VIEW',
                      cond: 'check_if_is_mobile',
                    },
                    {
                      target: 'CHOOSE_FLOW_TYPE',
                    },
                  ],
                },
                OPENING_BANK_ID_VIEW: {
                  entry: ['open_bank_id', 'set_user_click_open_bankid_in_context'],
                },
                REMOTE_VIEW: {},
                MOBILE_SELECT_TARGET_VIEW: {
                  on: {
                    USER_CLICK_REMOTE_OPEN_BANK_ID_EVENT: {
                      target: 'CHOOSE_FLOW_TYPE',
                      actions: 'set_remote_in_context',
                    },
                    USER_CLICK_LOCAL_OPEN_BANK_ID_EVENT: {
                      target: 'OPENING_BANK_ID_VIEW',
                      actions: 'set_local_in_context',
                    },
                  },
                },
                CHOOSE_FLOW_TYPE: {
                  always: [
                    {
                      target: '#BankIdMachine.HANDOFF_CREATOR_MACHINE',
                      cond: 'check_if_bank_id_token_exist_and_provider_is_handoff_specific',
                    },
                    {
                      target: 'REMOTE_VIEW',
                      cond: 'check_if_bank_id_token_exist',
                    },
                    {
                      target: 'OPENING_BANK_ID_VIEW',
                    },
                  ],
                },
              },
            },
          },
        },
        HANDOFF_CREATOR_MACHINE: {
          on: {
            HANDOFF_DONE_EVENT: {
              target: 'DONE',
              actions: 'set_credentials_in_context',
            },
          },
        },
        CHECK_FLOW_FINISHED: {
          always: [
            {
              target: 'FLOW_FINISHED_VIEW',
              cond: 'check_if_flow_finished',
            },
            {
              target: 'DONE',
            },
          ],
        },
        DONE: {
          entry: 'execute_on_done',
          type: 'final',
        },
        FLOW_FINISHED_VIEW: {
          type: 'final',
        },
        APPLICATION_ERROR: {
          type: 'final',
        },
      },
    },
    {
      guards: {
        check_if_credentials_in_invalid_state: ({ credentials }) =>
          !Credentials.isAwaitingMobileBankId(credentials),
        check_if_forced_remote: ({ bankIdTarget }) =>
          bankIdTarget === AuthenticationPromptType.REMOTE,
        check_if_forced_local: ({ bankIdTarget }) =>
          bankIdTarget === AuthenticationPromptType.LOCAL,
        check_if_is_mobile: ({ isMobile }) => isMobile,
        check_if_bank_id_token_exist_and_provider_is_handoff_specific: ({ credentials }) =>
          !!credentials.supplementalInformation &&
          getBankIdAuthenticationType(credentials.providerName) ===
            BankIdAuthenticationType.BANK_ID_AUTO_START_TOKEN &&
          Provider.isRequiringHandoffQrCode(credentials.providerName),
        check_if_bank_id_token_exist: ({ credentials }) => !!credentials.supplementalInformation,
        check_if_credentials_status_changed: ({ credentials }) =>
          !Credentials.isAwaitingMobileBankId(credentials),
        check_if_flow_finished: ({ userClickedOpenBankId, canFinishFlow }) =>
          Boolean(userClickedOpenBankId) && canFinishFlow,
      },
      actions: {
        set_user_click_open_bankid_in_context: assign({ userClickedOpenBankId: (_) => true }),
        set_remote_in_context: assign({ bankIdTarget: (_) => AuthenticationPromptType.REMOTE }),
        set_local_in_context: assign({ bankIdTarget: (_) => AuthenticationPromptType.LOCAL }),
        open_bank_id: ({ credentials, onAutoOpenBankIdWithAnalytics }) =>
          onAutoOpenBankIdWithAnalytics(credentials),
        set_credentials_in_context: assign({ credentials: (_, event) => event.credentials }),
        update_credentials_in_context: assign({ credentials: (_, event) => event.data }),
        set_error_in_context: assign({ error: (_, event) => event.data as ApplicationError }),
        execute_on_done: ({ credentials }) => onDone(credentials),
        throw_credentials_invalid_state_error: assign({
          error: (_) => new ApplicationError(CREDENTIALS_STATE_INVALID),
        }),
      },
      services: {
        getCredentials: ({ credentials }) => service.getCredentials(credentials.id),
      },
      delays: {
        POLL_INTERVAL: pollInterval,
      },
    }
  );
